import { FirefinchUserStoryDto } from '../../../api/models';

export interface TimelinePhase {
    name: string;
    startDate?: Date;
    endDate?: Date;
    duration?: number; // in weeks
    userStories: FirefinchUserStoryDto[];
    description?: string;
}

export interface TimelineConfig {
    projectStartDate?: Date;
    defaultPhaseDuration: number; // weeks
    title?: string;
    dateFormat?: string;
    includeUserStories?: boolean;
    includeMilestones?: boolean;
}

export const defaultTimelineConfig: TimelineConfig = {
    defaultPhaseDuration: 2,
    title: 'Project Timeline',
    dateFormat: '%m/%d/%Y',
    includeUserStories: false,
    includeMilestones: true
};

export function generateTimelineFromPhases(
    phases: TimelinePhase[],
    config: TimelineConfig = defaultTimelineConfig
): string {
    const mergedConfig = { ...defaultTimelineConfig, ...config };

    let mermaidCode = `%%{init: {'themeVariables': {'fontSize': '40px', 'timelineSectionFontSize': '500px'}}}%%\n`;
    mermaidCode += `timeline\n`;
    mermaidCode += `    title ${mergedConfig.title}\n`;

    let sprintCounter = 1; // Global sprint counter across all phases

    phases.forEach(phase => {
        // Calculate adjusted phase duration for display
        const baseDuration =
            phase.duration ?? mergedConfig.defaultPhaseDuration;
        const adjustedDuration = baseDuration * 1.25; // Utility loss adjustment (1/0.8 = 1.25)
        const roundedDuration = Math.round(adjustedDuration * 10) / 10;

        // Add phase section with duration in title
        mermaidCode += `    section ${phase.name} (${roundedDuration} weeks)\n`;

        // Calculate sprints based on phase duration (2 weeks per sprint)
        const numberOfSprints = Math.ceil(adjustedDuration / 2); // 2 weeks per sprint

        // Generate sprints for this phase
        if (mergedConfig.includeUserStories && phase.userStories.length > 0) {
            // Distribute user stories evenly across sprints
            const storiesPerSprint = Math.ceil(
                phase.userStories.length / numberOfSprints
            );

            for (let i = 0; i < numberOfSprints; i++) {
                const startIndex = i * storiesPerSprint;
                const endIndex = Math.min(
                    startIndex + storiesPerSprint,
                    phase.userStories.length
                );
                const sprintStories = phase.userStories.slice(
                    startIndex,
                    endIndex
                );

                if (sprintStories.length > 0) {
                    const storyTasks = sprintStories
                        .map(story => {
                            if (story.goal) {
                                // Truncate and format goal text
                                return story.goal.length > 15
                                    ? `${story.goal.substring(0, 15).replace(/\s+/g, ' ').trim()}...`
                                    : story.goal
                                          .replace(/\s+/g, ' ')
                                          .replace(/:/g, '');
                            }
                            return (
                                story.reqId ??
                                `Task ${sprintStories.indexOf(story) + 1}`
                            );
                        })
                        .join(': ');

                    mermaidCode += `        Sprint ${sprintCounter}: ${storyTasks}\n`;
                } else {
                    mermaidCode += `        Sprint ${sprintCounter}: Dev Work\n`;
                }
                sprintCounter++; // Increment global counter
            }
        } else {
            // Default sprint generation without user stories
            for (let i = 0; i < numberOfSprints; i++) {
                mermaidCode += `        Sprint ${sprintCounter}: Dev Work\n`;
                sprintCounter++; // Increment global counter
            }
        }

        // Add phase duration note (already included in section title, so we can remove this)
        // mermaidCode += `        Phase Duration: ${roundedDuration} weeks (incl. utility losses)\n`;

        // Add milestone if enabled
        if (mergedConfig.includeMilestones) {
            mermaidCode += `        ▲ Milestone: ${phase.name} Complete\n`;
        }
    });

    // Note: Disclaimer message moved to component text outside the diagram

    return mermaidCode;
}

export function generateTimelineFromUserStories(
    userStories: FirefinchUserStoryDto[],
    config: TimelineConfig = defaultTimelineConfig
): string {
    // Group user stories by phase
    const phaseGroups = new Map<string, FirefinchUserStoryDto[]>();

    userStories.forEach(story => {
        const phaseName = story.phase?.name ?? 'No Phase';
        if (!phaseGroups.has(phaseName)) {
            phaseGroups.set(phaseName, []);
        }
        phaseGroups.get(phaseName)!.push(story);
    });

    // Convert to TimelinePhase objects
    const phases: TimelinePhase[] = Array.from(phaseGroups.entries()).map(
        ([phaseName, stories]) => ({
            name: phaseName,
            userStories: stories,
            duration: config.defaultPhaseDuration
        })
    );

    return generateTimelineFromPhases(phases, config);
}
